/* Turning decoder output into candidates, with no decoder in scope.
 *
 * Split out from `barcodeDecoder.ts` so the rules are testable in Node without
 * instantiating a 1 MiB WASM module: what counts as a read worth showing, how a
 * multi-symbol frame is ordered, and how zxing's result shape maps onto ours.
 */

/** The subset of zxing-wasm's ReadResult we actually depend on. */
export interface DecodedSymbol {
  text: string;
  format: string;
  isValid: boolean;
  error?: string;
  /** Corner points, present on real reads. Used only for ordering. */
  position?: {
    topLeft: { x: number; y: number };
    topRight: { x: number; y: number };
    bottomRight: { x: number; y: number };
    bottomLeft: { x: number; y: number };
  };
}

export interface DecodeCandidate {
  raw: string;
  format: string;
}

/**
 * Keep only reads we would be willing to write.
 *
 * A barcode that decodes but fails its own checksum is a NON-READ, not a
 * maybe-read: zxing sets `isValid` false and/or fills `error`. Surfacing those
 * as "did you mean" invites a technician to confirm a corrupt serial, and a
 * corrupt asset serial can never be reconciled against the unit on the wall.
 * Drop it and let the next frame try.
 */
export function usableSymbols(symbols: readonly DecodedSymbol[]): DecodedSymbol[] {
  return symbols.filter(
    (s) => s.isValid && !s.error && typeof s.text === 'string' && s.text.trim().length > 0,
  );
}

/**
 * Order the symbols in one frame top-to-bottom, then left-to-right.
 *
 * Matters for bulk scanning: a phone held over a pallet can catch three labels
 * in a frame, and the technician's mental model is reading order. Without this
 * the accumulated list comes out in whatever order the decoder happened to
 * finish in, which looks like the scanner is skipping around.
 */
export function inReadingOrder(symbols: readonly DecodedSymbol[]): DecodedSymbol[] {
  return [...symbols].sort((a, b) => {
    const ay = a.position?.topLeft.y ?? 0;
    const by = b.position?.topLeft.y ?? 0;
    if (ay !== by) return ay - by;
    const ax = a.position?.topLeft.x ?? 0;
    const bx = b.position?.topLeft.x ?? 0;
    return ax - bx;
  });
}

/** Full frame -> candidates, in the order they should be offered to the session. */
export function candidatesFromFrame(symbols: readonly DecodedSymbol[]): DecodeCandidate[] {
  return inReadingOrder(usableSymbols(symbols)).map((s) => ({
    raw: s.text,
    format: s.format,
  }));
}
